// /prospectos — tenant workspace: the prospects kanban. A funnel board;
// each lead moves right as it progresses. Cards move by drag (desktop) or via an
// accessible per-card menu (touch/keyboard). Search + filters narrow the board.
// Moves persist via moveLead() (PATCH /leads/:id); new prospects via createLead()
// (POST /leads). Data via the getLeadsBoard() seam -> GET /leads.
//
// FILTERS (CP3). "Asignado a" and "Área de interés" are multi-select: any of the
// ticked people OR any of the ticked áreas, and the two groups AND together —
// "las mías y las de Ana, en cardiología o pediatría" is one question, and it
// used to take one hardcoded chip ("Mis prospectos") that could only answer a
// third of it. Both live in the URL, so the view survives a refresh and can be
// sent to somebody; both are resolved SERVER-side, inside the tenant's
// visibility policy — the board is capped at 300 cards, so narrowing it in the
// browser would hide cards instead of finding them.

import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { type DragEvent, forwardRef, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { EMPTY_FICHA_HINT, LeadFieldInputs } from './lead-fields-form.js'
import {
  createLead,
  getLeadsBoard,
  type Lead,
  type LeadMetaKind,
  type LeadStage,
  moveLead,
  type StageKind,
} from './leads-data.js'
import {
  EmptyStateCard,
  Button,
  Checkbox,
  Chip,
  DropdownMenu,
  FilterBar,
  FilterButton,
  FormField,
  Input,
  Modal,
  PageHeader,
  Popover,
  SearchInput,
  Select,
  Spinner,
  toast,
} from '../../design-system/index.js'
import { apiErrorMessage } from '../../lib/api-error.js'
import { QueryError } from '../../lib/QueryError.js'
import { matchesSearch } from '../../lib/search.js'
import { useAssignableMembers } from '../oversight/useAssignableMembers.js'
import { stageOwnerLabel } from '../settings/pipelines-data.js'

/** Cómo se pinta el borde de una tarjeta según dónde terminó su recorrido: en
 *  verde si convirtió, en rojo si no. Una etapa abierta no se pinta — el color
 *  marca el final, y un tablero donde todo tiene color no señala nada. */
const CARD_BORDER: Record<StageKind, string> = {
  open: 'border-line',
  won: 'border-[color-mix(in_srgb,var(--ok)_45%,transparent)]',
  lost: 'border-[color-mix(in_srgb,var(--bad)_45%,transparent)]',
}

/** "Ana Ruiz" → "Ana R.". La cabecera de una columna mide 256px y ya lleva el
 *  nombre de la etapa; el apellido completo la parte en dos renglones. */
function shortName(name: string): string {
  const [first, second] = name.trim().split(/\s+/)
  if (!first) return name
  return second ? `${first} ${second.charAt(0)}.` : first
}

/** Quién toma el trabajo en esta columna, en dos o tres palabras — o null
 *  cuando lo atiende el Agente: es el actor por omisión, y una etiqueta en
 *  todas las columnas no distingue ninguna. */
function ownerChip(stage: LeadStage): string | null {
  if (stage.ownerType === 'bot') return null
  if (stage.ownerType === 'member') return shortName(stage.ownerName ?? 'Una persona')
  // La política nombrada se nombra — el NOMBRE, nunca la palabra "reglas".
  if (stage.teamPolicyName) return `Equipo · ${stage.teamPolicyName}`
  if (stage.teamPolicy === 'balanced') return 'Equipo · Equilibrada'
  // 'none' y null significan lo mismo: la columna es del equipo, sin
  // asignación automática. El tooltip lleva la frase completa.
  return 'Equipo'
}

/** Cómo se nombra un rol del espacio en la lista de personas. Mismo vocabulario
 *  que Configuración → Equipo. */
const TENANT_ROLE_LABEL: Record<string, string> = {
  tenant_owner: 'Dueño',
  tenant_admin: 'Administrador',
  tenant_agent: 'Asesor',
  tenant_viewer: 'Visor',
}

interface FilterOption {
  value: string
  label: string
  /** Segunda línea de la opción (el rol de la persona). */
  description?: string
}

/** `?asignado=me,uuid` → `['me','uuid']`; ausente o vacío → `[]`. */
function readList(raw: string | null): string[] {
  return (raw ?? '')
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part !== '')
}

/** Un filtro de varias opciones: casillas dentro de un popover, porque marcar
 *  dos personas no es elegir entre ellas. El botón dice cuántas van marcadas —
 *  el estado activo no puede ser solo un color.
 *
 *  F16 — mientras el popover está ABIERTO, la lista y las palomitas son
 *  fotografías locales (tomadas al abrirse). Cada palomita sigue actualizando
 *  la URL y el tablero al instante, pero el refetch que dispara ya no puede
 *  reconstruir la lista bajo el puntero: las facetas frescas se aplican hasta
 *  cerrar. Un área marcada que desaparece de las facetas nuevas sigue visible
 *  (y marcada) mientras el popover siga abierto. */
function FilterDropdown({
  label,
  options,
  selected,
  onToggle,
}: {
  label: string
  options: FilterOption[]
  selected: string[]
  onToggle: (value: string) => void
}): JSX.Element {
  const [open, setOpen] = useState(false)
  const [snapshot, setSnapshot] = useState<FilterOption[]>([])
  const [localSelected, setLocalSelected] = useState<string[]>([])

  function handleOpenChange(next: boolean): void {
    // Seed the snapshot from the live props at the moment it opens; while it
    // stays open, refetched props no longer reach the rendered list.
    if (next) {
      setSnapshot(options)
      setLocalSelected(selected)
    }
    setOpen(next)
  }

  function toggle(value: string): void {
    setLocalSelected((cur) =>
      cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value],
    )
    onToggle(value)
  }

  const shown = open ? snapshot : options
  const ticked = open ? localSelected : selected
  return (
    <Popover
      label={label}
      placement="bottom"
      className="max-h-[320px] w-[248px] overflow-y-auto p-2"
      open={open}
      onOpenChange={handleOpenChange}
      trigger={
        <FilterButton
          active={selected.length > 0}
          {...(selected.length > 0 && { count: selected.length })}
        >
          {label}
        </FilterButton>
      }
    >
      <div className="flex flex-col gap-0.5">
        {shown.map((o) => (
          <Checkbox
            key={o.value}
            checked={ticked.includes(o.value)}
            onChange={() => toggle(o.value)}
            label={o.label}
            {...(o.description ? { description: o.description } : {})}
            className="rounded-md px-1.5 py-1.5 hover:bg-hover"
          />
        ))}
      </div>
    </Popover>
  )
}

/** Una selección activa, con su propia salida. Un filtro que solo se ve dentro
 *  del desplegable que lo puso es un filtro que se olvida encendido. */
function ActiveFilterChip({
  label,
  onRemove,
}: {
  label: string
  onRemove: () => void
}): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1 rounded-pill bg-[color-mix(in_srgb,var(--brand-blue)_10%,transparent)] py-1 pl-2.5 pr-1 text-[11.5px] font-semibold text-link">
      {label}
      <button
        type="button"
        aria-label={`Quitar filtro ${label}`}
        onClick={onRemove}
        className="grid size-[17px] place-items-center rounded-full text-link transition-colors hover:bg-[color-mix(in_srgb,var(--brand-blue)_18%,transparent)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-blue [&_svg]:size-[11px]"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.6"
          strokeLinecap="round"
          aria-hidden
        >
          <path d="M6 6l12 12M18 6 6 18" />
        </svg>
      </button>
    </span>
  )
}

function MetaIcon({ kind }: { kind: LeadMetaKind }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {kind === 'time' && (
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" />
        </>
      )}
      {kind === 'date' && (
        <>
          <rect x="3" y="4.5" width="18" height="17" rx="2.4" />
          <path d="M3 9.5h18M8 2.5v4M16 2.5v4" />
        </>
      )}
      {kind === 'done' && <path d="M20 6 9 17l-5-5" />}
    </svg>
  )
}

const MoveTrigger = forwardRef<HTMLButtonElement, { name: string }>(function MoveTrigger(
  { name, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      aria-label={`Mover ${name}`}
      className="-mr-1 -mt-0.5 flex size-6 flex-none items-center justify-center rounded-md text-text-3 transition-colors hover:bg-hover hover:text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-blue [&_svg]:size-[16px]"
      {...props}
    >
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <circle cx="5" cy="12" r="1.7" />
        <circle cx="12" cy="12" r="1.7" />
        <circle cx="19" cy="12" r="1.7" />
      </svg>
    </button>
  )
})

function LeadCard({
  lead,
  kind,
  stages,
  onMove,
  onOpen,
  onDragStart,
  onDragEnd,
  dragging,
}: {
  lead: Lead
  /** El tipo de la columna en la que está la tarjeta. Se lee de la etapa y no
   *  de `lead.won`, para que ganada y perdida se pinten por la misma regla. */
  kind: StageKind
  stages: LeadStage[]
  onMove: (stageId: string) => void
  onOpen: () => void
  onDragStart: () => void
  onDragEnd: () => void
  dragging: boolean
}): JSX.Element {
  return (
    <article
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className={`cursor-grab rounded-[10px] border bg-surface p-3 shadow-card transition-shadow active:cursor-grabbing hover:shadow-card-hover ${
        CARD_BORDER[kind]
      } ${dragging ? 'opacity-50' : ''}`}
    >
      <div className="flex items-start gap-2">
        <button
          type="button"
          onClick={onOpen}
          className="min-w-0 flex-1 text-left text-[12.5px] font-semibold text-text hover:text-link focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue"
        >
          {lead.name}
        </button>
        <DropdownMenu
          trigger={<MoveTrigger name={lead.name} />}
          items={stages
            .filter((s) => s.id !== lead.stageId)
            .map((s) => ({ label: `Mover a ${s.name}`, onSelect: () => onMove(s.id) }))}
        />
      </div>

      <div className="mt-2">
        <Chip tone={lead.specialty.tone} dot>
          {lead.specialty.label}
        </Chip>
      </div>

      {lead.captured && lead.captured.length > 0 ? (
        <dl className="mt-2 flex flex-col gap-0.5">
          {lead.captured.slice(0, 3).map((f) => (
            <div key={f.label} className="flex gap-1.5 text-[10.5px] leading-snug">
              <dt className="flex-none font-semibold text-text-3">{f.label}:</dt>
              <dd className="min-w-0 truncate text-text-2">{f.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      <div className="mt-2.5 flex items-center gap-2">
        {lead.stale ? (
          <span className="flex items-center gap-1 rounded-[5px] bg-warn-bg px-1.5 py-0.5 text-[10px] font-semibold text-warn [&_svg]:size-[10px]">
            <MetaIcon kind="time" />
            {lead.staleText}
          </span>
        ) : (
          <span className="flex items-center gap-1 text-[10px] text-text-3 [&_svg]:size-[11px]">
            <MetaIcon kind={lead.metaKind} />
            {lead.meta}
          </span>
        )}
        <span
          aria-hidden
          title={lead.assignee ? `Asignado a ${lead.assignee}` : 'Sin asignar'}
          className={`ml-auto grid size-[22px] flex-none place-items-center rounded-full text-[8.5px] font-bold ${
            lead.assignee
              ? 'bg-[image:var(--brand-gradient)] text-white'
              : 'border border-dashed border-line text-text-3'
          }`}
        >
          {lead.assignee ?? '?'}
        </span>
      </div>
    </article>
  )
}

export function LeadsPage(): JSX.Element {
  const navigate = useNavigate()
  const qc = useQueryClient()
  // Todo lo que decide QUÉ pide el tablero vive en la URL: al recargar se ve lo
  // mismo, y el enlace se puede mandar. Los tres parámetros se componen entre
  // sí — el proceso elegido no borra por quién estabas filtrando.
  const [searchParams, setSearchParams] = useSearchParams()
  const assignees = useMemo(() => readList(searchParams.get('asignado')), [searchParams])
  const areas = useMemo(() => readList(searchParams.get('area')), [searchParams])
  // Which funnel the board is showing. '' means "whichever one the server calls
  // default" — the state a tenant with one pipeline never leaves, and the state
  // this page is in before the first response tells it what funnels exist.
  const pipelineId = searchParams.get('proceso') ?? ''

  /** Escribe (o borra) un parámetro de filtro sin tocar los demás. `replace`
   *  porque marcar tres casillas no son tres pasos atrás. */
  function writeParam(key: string, values: string[]): void {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current)
        if (values.length === 0) next.delete(key)
        else next.set(key, values.join(','))
        return next
      },
      { replace: true },
    )
  }

  function toggleFilter(key: string, current: string[], value: string): void {
    writeParam(
      key,
      current.includes(value) ? current.filter((v) => v !== value) : [...current, value],
    )
  }

  // Filtros SERVER-side (el tablero está topado a 300 tarjetas), así que son
  // parte de la query key: cada combinación es otra pregunta, no otro recorte
  // de la misma respuesta.
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['leads', pipelineId, assignees.join(','), areas.join(',')],
    queryFn: () =>
      getLeadsBoard({ assignees, areas, ...(pipelineId === '' ? {} : { pipelineId }) }),
    // F16: cada combinación de filtros es otra queryKey, y sin esto `data`
    // queda undefined mientras llega la respuesta — la página entera se iba al
    // Spinner en cada palomita, desmontando el popover abierto bajo el clic.
    // Con la respuesta anterior en pantalla, filtrar es un repintado, no un
    // "refresh".
    placeholderData: keepPreviousData,
  })
  // Quién puede tener prospectos, para la lista de personas del filtro. Es la
  // misma lista que ofrece Asignar en la ficha, así que no se puede filtrar por
  // alguien a quien no se le puede asignar.
  const { members: assignables } = useAssignableMembers()
  const [leads, setLeads] = useState<Lead[]>([])
  const [view, setView] = useState<'board' | 'list'>('board')
  const [query, setQuery] = useState('')
  // El chip de seguimiento. Se llamaba `slaOnly` por la columna muerta que
  // filtraba; ahora se llama por lo que pregunta.
  const [staleOnly, setStaleOnly] = useState(false)
  const [draggingId, setDraggingId] = useState<string | null>(null)
  const [overStage, setOverStage] = useState<string | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [newOrigin, setNewOrigin] = useState('')
  // Lo que el modal capturó del catálogo, por clave. Se vacía al crear.
  const [newFields, setNewFields] = useState<Record<string, string>>({})

  function open(leadId: string): void {
    void navigate(`/prospectos/${leadId}`)
  }

  // F22 — edge auto-scroll while dragging. The columns container clips at
  // overflow-x, and native HTML5 DnD does not scroll it, so a column off-screen
  // was unreachable by drag. While a card is in the air, an rAF loop nudges
  // scrollLeft whenever the pointer sits within EDGE px of either side. Only
  // the horizontal axis is touched — vertical page scroll stays native.
  const boardRef = useRef<HTMLDivElement>(null)
  const dragX = useRef<number | null>(null)
  useEffect(() => {
    if (!draggingId) return
    const EDGE = 80
    const MAX_STEP = 18
    let raf = 0
    const step = (): void => {
      const el = boardRef.current
      const x = dragX.current
      if (el && x !== null) {
        const rect = el.getBoundingClientRect()
        // Speed grows as the pointer nears the edge, so a grazing pass drifts
        // and a pinned pointer travels.
        if (x < rect.left + EDGE) {
          el.scrollLeft -= Math.ceil(((rect.left + EDGE - x) / EDGE) * MAX_STEP)
        } else if (x > rect.right - EDGE) {
          el.scrollLeft += Math.ceil(((x - (rect.right - EDGE)) / EDGE) * MAX_STEP)
        }
      }
      raf = requestAnimationFrame(step)
    }
    raf = requestAnimationFrame(step)
    return () => {
      cancelAnimationFrame(raf)
      dragX.current = null
    }
  }, [draggingId])

  useEffect(() => {
    if (data) setLeads(data.leads)
  }, [data])

  const stages = data?.stages ?? []
  // Solo-mode rule: one funnel is not a choice, so there is no control. The
  // selector appears the moment a second one exists and disappears again if it
  // is deleted.
  const pipelines = data?.pipelines ?? []
  const showPipelinePicker = pipelines.length > 1
  // What a create posts. The board's current funnel, resolved to a real id even
  // while the selector still reads '' — a card must land in the funnel the
  // person is looking at, not in whichever one is default.
  const activePipelineId = data?.pipeline?.id ?? ''

  // Las áreas que existen en ESTE proceso, ya filtradas por el servidor con la
  // misma política de visibilidad del tablero. Vacío = el filtro no se dibuja:
  // un desplegable sin opciones es una puerta pintada.
  const facetAreas = data?.facets?.areas ?? []
  // La ficha de ESTE proceso: lo que el modal de alta pide. Es el mismo
  // vocabulario que captura el Agente y que requieren las etapas, así que un
  // prospecto tecleado a mano puede entrar donde entra uno capturado.
  const leadFields = data?.fields ?? []

  // Al cambiar de proceso, las personas siguen valiendo pero un área que no
  // existe en el nuevo dejaría el tablero vacío sin decir por qué. Se sueltan
  // en cuanto llega la respuesta que prueba que ya no están.
  useEffect(() => {
    if (!data) return
    const kept = areas.filter((a) => data.facets.areas.includes(a))
    if (kept.length !== areas.length) writeParam('area', kept)
    // `writeParam` is deliberately not a dependency: it closes over nothing but
    // the router setter, and re-running on its identity would loop.
  }, [data, areas])

  const peopleOptions = useMemo<FilterOption[]>(
    () => [
      { value: 'me', label: 'Mí' },
      { value: 'none', label: 'Sin asignar' },
      ...assignables.map((m) => ({
        value: m.id,
        label: m.name,
        ...(TENANT_ROLE_LABEL[m.role] ? { description: TENANT_ROLE_LABEL[m.role]! } : {}),
      })),
    ],
    [assignables],
  )
  const areaOptions = useMemo<FilterOption[]>(
    () => facetAreas.map((a) => ({ value: a, label: a })),
    [facetAreas],
  )

  /** Cómo se lee una selección en su chip: la persona por su nombre, el área
   *  por sí misma. Un id crudo en pantalla no le dice nada a nadie. */
  function peopleLabel(value: string): string {
    return peopleOptions.find((o) => o.value === value)?.label ?? value
  }

  const hasFilters = query.trim() !== '' || staleOnly || assignees.length > 0 || areas.length > 0

  function clearFilters(): void {
    setQuery('')
    setStaleOnly(false)
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current)
        next.delete('asignado')
        next.delete('area')
        return next
      },
      { replace: true },
    )
  }

  // C8: day one, zero leads, no filters — an empty funnel with no explanation
  // reads as "broken", when the truth is "your agent creates these".
  const boardEmpty = !isLoading && !isError && leads.length === 0 && !hasFilters

  const visible = useMemo(
    () => leads.filter((l) => matchesSearch(l.name, query) && (!staleOnly || l.stale)),
    [leads, query, staleOnly],
  )

  // Filtros puestos, cero coincidencias. Hay que decirlo: seis columnas vacías
  // se leen como "no hay prospectos", que es justo lo que no pasó.
  const noMatches = !boardEmpty && hasFilters && visible.length === 0

  /** Whether a stage is the funnel's win — read from the payload, never from
   *  the stage's name: the tenant chooses what its stages are called. */
  function isWon(stageId: string): boolean {
    return stages.find((s) => s.id === stageId)?.kind === 'won'
  }

  function move(leadId: string, stageId: string): void {
    const prev = leads.find((l) => l.id === leadId)?.stageId
    setLeads((cur) =>
      cur.map((l) => (l.id === leadId ? { ...l, stageId, won: isWon(stageId) } : l)),
    )
    if (prev === stageId) return
    moveLead(leadId, stageId)
      .then(() => qc.invalidateQueries({ queryKey: ['leads'] }))
      .catch((err: unknown) => {
        // revert the optimistic move
        setLeads((cur) =>
          cur.map((l) => (l.id === leadId && prev ? { ...l, stageId: prev, won: isWon(prev) } : l)),
        )
        // El servidor ya dijo POR QUÉ ("Faltan datos para entrar a Cita
        // agendada: Especialidad."). Tirar esa frase y decir "inténtalo de
        // nuevo" mandaba a la gente a repetir el mismo arrastre para siempre.
        toast.error('No se pudo mover', {
          description: apiErrorMessage(err, 'Inténtalo de nuevo.'),
        })
      })
  }

  async function submitCreate(): Promise<void> {
    if (!newName.trim()) {
      toast.error('Falta el nombre', { description: 'Escribe el nombre del prospecto.' })
      return
    }
    setCreating(true)
    try {
      // Solo lo que se llenó: una clave vacía no es una respuesta en blanco, es
      // una pregunta sin contestar.
      const filled = Object.fromEntries(
        Object.entries(newFields).filter(([, v]) => v.trim() !== ''),
      )
      await createLead({
        name: newName.trim(),
        ...(Object.keys(filled).length > 0 ? { fields: filled } : {}),
        ...(newOrigin.trim() ? { origin: newOrigin.trim() } : {}),
        ...(activePipelineId === '' ? {} : { pipelineId: activePipelineId }),
      })
      await qc.invalidateQueries({ queryKey: ['leads'] })
      toast.ok('Prospecto creado')
      setCreateOpen(false)
      setNewName('')
      setNewOrigin('')
      setNewFields({})
    } catch (err) {
      // Igual que el arrastre: la etapa de entrada puede exigir datos, y la
      // frase del servidor los nombra uno por uno.
      toast.error('No se pudo crear', { description: apiErrorMessage(err, 'Inténtalo de nuevo.') })
    } finally {
      setCreating(false)
    }
  }

  function onDrop(e: DragEvent, stageId: string): void {
    e.preventDefault()
    if (draggingId) move(draggingId, stageId)
    setDraggingId(null)
    setOverStage(null)
  }

  if (isError) {
    return (
      <div data-testid="leads" className="py-10">
        <QueryError message="No pudimos cargar tus prospectos." onRetry={() => void refetch()} />
      </div>
    )
  }
  if (isLoading || !data) {
    return (
      <div data-testid="leads" className="py-10">
        <Spinner label="Cargando prospectos…" />
      </div>
    )
  }

  return (
    <div data-testid="leads" className="flex h-full flex-col">
      <PageHeader
        title="Procesos"
        subtitle="Los recorridos de tus prospectos, proceso por proceso. Arrástralos o usa el menú de cada tarjeta para cambiar de etapa."
        actions={
          <>
            <div
              role="group"
              aria-label="Vista"
              className="flex flex-none gap-0.5 rounded-lg border border-line bg-surface-2 p-0.5"
            >
              {(['board', 'list'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  aria-pressed={view === v}
                  onClick={() => setView(v)}
                  className={`rounded-md px-2.5 py-1.5 text-[11.5px] font-semibold transition-colors ${
                    view === v ? 'bg-surface text-text shadow-card' : 'text-text-2 hover:text-text'
                  }`}
                >
                  {v === 'board' ? 'Tablero' : 'Lista'}
                </button>
              ))}
            </div>
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              Nuevo prospecto
            </Button>
          </>
        }
      />

      <div className="mt-4 flex-none">
        <FilterBar>
          <SearchInput
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar prospecto"
            aria-label="Buscar prospecto"
          />
          {showPipelinePicker && (
            <div className="w-full sm:w-48">
              <Select
                aria-label="Proceso"
                data-testid="pipeline-picker"
                value={pipelineId === '' ? activePipelineId : pipelineId}
                onChange={(e) => writeParam('proceso', [e.target.value])}
                options={pipelines.map((p) => ({ value: p.id, label: p.name }))}
              />
            </div>
          )}
          <FilterDropdown
            label="Asignado a"
            options={peopleOptions}
            selected={assignees}
            onToggle={(value) => toggleFilter('asignado', assignees, value)}
          />
          {areaOptions.length > 0 && (
            <FilterDropdown
              label="Área de interés"
              options={areaOptions}
              selected={areas}
              onToggle={(value) => toggleFilter('area', areas, value)}
            />
          )}
          {/* F21: "Sin seguimiento" se leía como pariente de "Sin asignar".
              Este chip habla de actividad, no de responsable.
              PR J: y ahora habla de una regla que existe. Filtraba por
              `sla_due_at`, una columna que nada escribía nunca, así que el chip
              siempre dejaba el tablero vacío mientras la notificación nocturna
              marcaba otras tarjetas. El título dice la regla completa para que
              nadie tenga que adivinarla. */}
          <FilterButton
            active={staleOnly}
            onClick={() => setStaleOnly((v) => !v)}
            title="Sin actividad en 3 días o más, en etapas abiertas que no son la de cita."
          >
            Necesitan seguimiento
          </FilterButton>
        </FilterBar>
        {(assignees.length > 0 || areas.length > 0 || hasFilters) && (
          <div data-testid="active-filters" className="mt-2 flex flex-wrap items-center gap-1.5">
            {assignees.map((value) => (
              <ActiveFilterChip
                key={`p-${value}`}
                label={peopleLabel(value)}
                onRemove={() => toggleFilter('asignado', assignees, value)}
              />
            ))}
            {areas.map((value) => (
              <ActiveFilterChip
                key={`a-${value}`}
                label={value}
                onRemove={() => toggleFilter('area', areas, value)}
              />
            ))}
            <button
              type="button"
              onClick={clearFilters}
              className="rounded-md px-1 text-[11.5px] font-semibold text-link hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue"
            >
              Limpiar filtros
            </button>
          </div>
        )}
      </div>

      {boardEmpty ? (
        <div className="mt-4">
          <EmptyStateCard
            kind="day1"
            icon={
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M4 19V5m5 14V9m5 10v-6m5 6V7" />
              </svg>
            }
            title="Aún no hay prospectos"
            body="Se crean solos: cuando tu agente detecta interés en una conversación, captura al prospecto aquí con su etapa. También puedes esperar la primera campaña — no hay nada que configurar."
          />
        </div>
      ) : noMatches ? (
        <div className="mt-4 rounded-xl border border-line bg-surface px-4 py-12 text-center">
          {/* La salida ya está en la barra de arriba ("Limpiar filtros"), a la
              vista junto a los chips que causaron esto. */}
          <p className="text-[12.5px] text-text-2">Ningún prospecto coincide con los filtros.</p>
        </div>
      ) : view === 'list' ? (
        <ul className="mt-4 min-h-0 flex-1 divide-y divide-line overflow-y-auto rounded-xl border border-line bg-surface">
          {visible.map((lead) => (
            <li key={lead.id}>
              <button
                type="button"
                onClick={() => open(lead.id)}
                className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-hover focus-visible:outline focus-visible:-outline-offset-2 focus-visible:outline-2 focus-visible:outline-brand-blue"
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[12.5px] font-semibold text-text">
                    {lead.name}
                  </span>
                  <span className="mt-0.5 block text-[11px] text-text-3">
                    {lead.staleText ?? lead.meta}
                  </span>
                </span>
                <Chip tone={lead.specialty.tone} dot>
                  {lead.specialty.label}
                </Chip>
                <span className="hidden flex-none items-center gap-1.5 text-[11px] font-semibold text-text-2 sm:flex">
                  <span
                    aria-hidden
                    className="size-2 rounded-full"
                    style={{ backgroundColor: stages.find((s) => s.id === lead.stageId)?.dot }}
                  />
                  {stages.find((s) => s.id === lead.stageId)?.name}
                </span>
                <span
                  aria-hidden
                  className={`grid size-[22px] flex-none place-items-center rounded-full text-[8.5px] font-bold ${
                    lead.assignee
                      ? 'bg-[image:var(--brand-gradient)] text-white'
                      : 'border border-dashed border-line text-text-3'
                  }`}
                >
                  {lead.assignee ?? '?'}
                </span>
              </button>
            </li>
          ))}
          {visible.length === 0 && (
            <li className="px-4 py-10 text-center text-[12px] text-text-3">
              Ningún prospecto coincide.
            </li>
          )}
        </ul>
      ) : (
        <div
          ref={boardRef}
          // dragover bubbles from every column, so this is the one listener the
          // auto-scroll loop needs to know where the pointer is.
          onDragOver={(e) => {
            dragX.current = e.clientX
          }}
          className="mt-4 flex min-h-0 flex-1 gap-3 overflow-x-auto pb-2"
        >
          {stages.map((stage) => {
            const cards = visible.filter((l) => l.stageId === stage.id)
            const over = overStage === stage.id
            const owner = ownerChip(stage)
            return (
              <section
                key={stage.id}
                aria-label={stage.name}
                onDragOver={(e) => {
                  e.preventDefault()
                  setOverStage(stage.id)
                }}
                onDragLeave={() => setOverStage((s) => (s === stage.id ? null : s))}
                onDrop={(e) => onDrop(e, stage.id)}
                className={`flex w-[256px] flex-none flex-col rounded-xl border bg-surface-2 transition-colors ${
                  over
                    ? 'border-brand-blue bg-[color-mix(in_srgb,var(--brand-blue)_5%,transparent)]'
                    : 'border-line'
                }`}
              >
                <div className="flex flex-col gap-1 border-b border-line px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <span
                      aria-hidden
                      className="size-2 flex-none rounded-full"
                      style={{ backgroundColor: stage.dot }}
                    />
                    <span className="min-w-0 truncate text-[12px] font-bold text-text">
                      {stage.name}
                    </span>
                    <span className="flex-none rounded-pill bg-hover px-[7px] py-px text-[10.5px] font-bold tabular-nums text-text-3">
                      {cards.length}
                    </span>
                  </div>
                  {/* Quién se hace responsable aquí. Se configura en Procesos;
                      el tablero solo lo reporta, en una línea que se corta
                      antes de romper el ancho de la columna. */}
                  {owner !== null && (
                    <span
                      data-testid={`stage-owner-${stage.id}`}
                      title={`Responsable: ${stageOwnerLabel(
                        stage.ownerType,
                        stage.teamPolicy,
                        stage.ownerName ?? null,
                        stage.teamPolicyName ?? null,
                      )}`}
                      className="max-w-full self-start truncate rounded-pill bg-hover px-[7px] py-px text-[10px] font-semibold text-text-3"
                    >
                      {owner}
                    </span>
                  )}
                </div>
                <div className="flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto p-2.5">
                  {cards.map((lead) => (
                    <LeadCard
                      key={lead.id}
                      lead={lead}
                      kind={stage.kind}
                      stages={stages}
                      onMove={(stageId) => move(lead.id, stageId)}
                      onOpen={() => open(lead.id)}
                      onDragStart={() => setDraggingId(lead.id)}
                      onDragEnd={() => {
                        setDraggingId(null)
                        setOverStage(null)
                      }}
                      dragging={draggingId === lead.id}
                    />
                  ))}
                  {cards.length === 0 && (
                    <p className="px-1 py-6 text-center text-[11px] text-text-3">
                      Sin prospectos aquí.
                    </p>
                  )}
                </div>
              </section>
            )
          })}
        </div>
      )}

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Nuevo prospecto"
        subtitle="Agrega un prospecto manualmente al proceso."
        footer={
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCreateOpen(false)}
              disabled={creating}
            >
              Cancelar
            </Button>
            <Button
              size="sm"
              className="ml-auto"
              disabled={creating}
              onClick={() => void submitCreate()}
            >
              {creating ? 'Creando…' : 'Crear prospecto'}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3.5">
          <FormField label="Nombre" htmlFor="lead-name">
            <Input
              id="lead-name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Nombre del prospecto"
            />
          </FormField>
          {/* Los datos de este proceso, con su tipo. Antes esto era un "Área de
              interés" de texto libre que no era ningún campo del catálogo: lo
              que se escribía aquí no lo veía ninguna regla ni cumplía ningún
              requisito de etapa. */}
          {leadFields.length === 0 ? (
            // Un proceso sin datos deja un hueco entre Nombre y Origen que se
            // lee como una pantalla a medio cargar. La línea dice qué pasa y
            // dónde se arregla. Misma frase para todos los roles: a un Asesor
            // no le hace daño saber dónde vive la configuración.
            <p className="text-[12px] leading-relaxed text-text-2" data-testid="lead-fields-empty">
              {EMPTY_FICHA_HINT}
            </p>
          ) : (
            <LeadFieldInputs
              fields={leadFields}
              values={newFields}
              idPrefix="lead-field"
              disabled={creating}
              onChange={(key, value) => setNewFields((prev) => ({ ...prev, [key]: value }))}
            />
          )}
          {/* A5: era "Origen", con "WhatsApp" de ejemplo, al lado de un Canal
              que ya decía WhatsApp solo. Dos campos para el mismo hecho, y
              ninguno confiable. Este pregunta lo que solo una persona sabe:
              qué trajo a este prospecto. El canal por el que llegó lo deriva
              la ficha de la identidad del contacto. */}
          <FormField label="¿Cómo nos conoció?" htmlFor="lead-origin" required="optional">
            <Input
              id="lead-origin"
              value={newOrigin}
              onChange={(e) => setNewOrigin(e.target.value)}
              placeholder="p. ej. Recomendación, Campaña de Facebook, Pasó por la clínica"
            />
          </FormField>
        </div>
      </Modal>
    </div>
  )
}
